import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DIFFICULTIES, LANGUAGES, TASK_STATUSES, TASK_TYPES } from '@quizdb/common';
import { Icon } from '../common/Icon';
import { TaskListFilters } from './taskEndpoints';

/** Props for `TaskFilters` (QZ-D8). */
export interface TaskFiltersProps {
  filters: TaskListFilters;
  tags: readonly string[];
  /** The title of `filters.jobId`'s job, if loaded (QZ-D59); the chip falls back to a short id. */
  jobTitle?: string;
  onChange(filters: TaskListFilters): void;
}

type ClearableKey = 'tag' | 'taskType' | 'difficulty' | 'language';

/**
 * The task list's filter bar (QZ-D8, QZ-D15, QZ-D16, QZ-D47). Status is a segmented radio
 * group and always has a value; the other four are `form-select`s whose first "any" option
 * clears the filter. "Clear filters" appears once any of those four is set.
 */
export const TaskFilters = ({ filters, tags, jobTitle, onChange }: TaskFiltersProps) => {
  const { t } = useTranslation();

  const handleClearableChange =
    <K extends ClearableKey>(key: K) =>
    (e: ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      onChange({ ...filters, [key]: value === '' ? undefined : (value as TaskListFilters[K]) });
    };

  const filtered =
    filters.tag !== undefined ||
    filters.taskType !== undefined ||
    filters.difficulty !== undefined ||
    filters.language !== undefined;

  return (
    <div className="qz-filterbar">
      <div className="qz-segmented me-2" role="radiogroup" aria-label={t('task.filter.status')}>
        {TASK_STATUSES.map((status) => (
          <span key={status}>
            <input
              type="radio"
              name="task-filter-status"
              id={`task-filter-status-${status}`}
              value={status}
              checked={filters.status === status}
              onChange={() => onChange({ ...filters, status })}
            />
            <label htmlFor={`task-filter-status-${status}`}>{t(`task.list.tab.${status}`)}</label>
          </span>
        ))}
      </div>
      {filters.jobId && (
        <button
          type="button"
          className="qz-tag"
          onClick={() => onChange({ ...filters, jobId: undefined })}
        >
          {t('task.filter.job', { title: jobTitle ?? filters.jobId.slice(0, 8) })}
          <Icon name="xmark" />
        </button>
      )}
      <div>
        <label htmlFor="task-filter-tag" className="form-label d-block">
          {t('task.filter.tag')}
        </label>
        <select
          id="task-filter-tag"
          className="form-select form-select-sm"
          value={filters.tag ?? ''}
          onChange={handleClearableChange('tag')}
        >
          <option value="">{t('task.list.any')}</option>
          {tags.map((tag) => (
            <option key={tag} value={tag}>
              {tag}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="task-filter-taskType" className="form-label d-block">
          {t('task.filter.taskType')}
        </label>
        <select
          id="task-filter-taskType"
          className="form-select form-select-sm"
          value={filters.taskType ?? ''}
          onChange={handleClearableChange('taskType')}
        >
          <option value="">{t('task.list.any')}</option>
          {TASK_TYPES.map((taskType) => (
            <option key={taskType} value={taskType}>
              {t(`task.type.${taskType}`)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="task-filter-difficulty" className="form-label d-block">
          {t('task.filter.difficulty')}
        </label>
        <select
          id="task-filter-difficulty"
          className="form-select form-select-sm"
          value={filters.difficulty ?? ''}
          onChange={handleClearableChange('difficulty')}
        >
          <option value="">{t('task.list.any')}</option>
          {DIFFICULTIES.map((difficulty) => (
            <option key={difficulty} value={difficulty}>
              {t(`task.difficulty.${difficulty}`)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="task-filter-language" className="form-label d-block">
          {t('task.filter.language')}
        </label>
        <select
          id="task-filter-language"
          className="form-select form-select-sm"
          value={filters.language ?? ''}
          onChange={handleClearableChange('language')}
        >
          <option value="">{t('task.list.any')}</option>
          {LANGUAGES.map((language) => (
            <option key={language} value={language}>
              {t(`task.language.${language}`)}
            </option>
          ))}
        </select>
      </div>
      {filtered && (
        <button
          type="button"
          className="btn btn-link btn-sm ms-auto"
          onClick={() => onChange({ status: filters.status, jobId: filters.jobId })}
        >
          <Icon name="xmark" /> {t('task.list.clearFilters')}
        </button>
      )}
    </div>
  );
};

export default TaskFilters;
